import { z } from 'zod';
import { TypeOfAPIError, TypeOfAPIResponse } from '@ninja/api-client';
import { APIFactoryOptions, factory } from '../../config';

const planDetailsSchema = z.object({
	name: z.string(),
	code: z.string(),
	topHospitalList: z.string(),
	insuranceCompanyId: z.number(),
	annualLimitCover: z.string(),
	annualLimitPharmacy: z.string(),
	coPaymentsInPatient: z.string(),
	coPaymentsDiagnostic: z.string(),
	coPaymentsPharmacy: z.string(),
	coPaymentsLabTests: z.string(),
	coPaymentsOPConsultations: z.string(),
	coPaymentsPhysioTreatment: z.string(),
	coPaymentsMaternity: z.string(),
	coPaymentsDental: z.string(),
	coPaymentsOptical: z.string(),
	policyWordingUrlEn: z.string(),
	policyWordingUrlAr: z.string(),
	planTypeIds: z.array(z.number()),
	planKeyBenefits: z.array(z.object({ benefitCategoryId: z.number(), isKeyBenefit: z.boolean() })),
	planBenefits: z.array(z.object({ benefitId: z.number() })),
	benefitSummary: z.array(z.string()),
	planNetworks: z.array(z.object({ networkId: z.number() })),
	planExclusionMappings: z.array(z.object({ planExclusionId: z.number() })),
	planNetworkCounts: z.array(
		z.object({
			facilityTypeId: z.number(),
			count: z.number(),
			display: z.boolean(),
		})
	),
});

const quotePlanSchema = z.object({
	planId: z.number(),
	quoteResponseId: z.number(),
	networkGroupId: z.number(),
	planDetails: planDetailsSchema,
	quotePlanReferenceNumber: z.string(),
	premiumAmount: z.number(),
	vatAmount: z.number(),
});

export const responseSchema = z.object({
	encryptedJourneyId: z.string(),
	networkMappings: z
		.array(
			z.object({
				id: z.number(),
				isTopHospital: z.boolean(),
				facilityTypeId: z.number(),
				facilityName: z.string(),
			})
		)
		.optional(),
	quoteResponse: z.object({
		quoteRequestReferenceNumber: z.string(),
		totalInsuredLives: z.number().optional(),
		quoteStatus: z.number(),
		totalAvailablePlans: z.number(),
		quoteExpiry: z.string(),
	}),
	quotePlans: z.array(quotePlanSchema),
	quotePlansPrices: z.array(
		z.object({
			quoteResponseId: z.number(),
			memberPrice: z.number(),
			memberPriceIncludingVAT: z.number(),
			planId: z.number(),
			memberid: z.string(),
		})
	),
});

const getQuoteList = (options: APIFactoryOptions) =>
	factory(options).createAPIDefinition({
		url: 'Quote/:journeyId/:quoteReference',
		method: 'GET',
		responseSchema,
		transformResponse: (response) => response.data.qualifiedPlans,
		requestParameters: { journeyId: ['required', 'string'], quoteReference: ['required', 'string'] },
	});

export default getQuoteList;

export type GetQuoteListResponse = TypeOfAPIResponse<ReturnType<typeof getQuoteList>>;

export type GetQuoteListError = TypeOfAPIError<ReturnType<typeof getQuoteList>>;

export type PlanDetailsSchema = z.infer<typeof planDetailsSchema>;

export type QuotePlan = z.infer<typeof quotePlanSchema>;
